import { useRef } from 'react';

type SwipeDirection = 'left' | 'right' | null;

interface UseSwipeOptions {
  minDistance?: number;
  minVelocity?: number;
  onSwipe: (direction: SwipeDirection) => void;
}

export function useSwipe({ minDistance = 50, minVelocity = 0.3, onSwipe }: UseSwipeOptions) {
  const touchStartX = useRef<number | null>(null);
  const touchStartTime = useRef<number | null>(null);
  const hasSwiped = useRef(false);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartTime.current = Date.now();
    hasSwiped.current = false;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (hasSwiped.current || touchStartX.current === null || touchStartTime.current === null) {
      return;
    }

    const currentX = e.touches[0].clientX;
    const deltaX = touchStartX.current - currentX;
    const deltaTime = Date.now() - touchStartTime.current;
    const velocity = deltaX / deltaTime;

    const fastEnough = Math.abs(velocity) > minVelocity;
    const farEnough = Math.abs(deltaX) > minDistance;

    if (fastEnough || farEnough) {
      const direction: SwipeDirection = deltaX > 0 ? 'left' : 'right';
      onSwipe(direction);
      hasSwiped.current = true;
    }
  };

  const onTouchEnd = () => {
    touchStartX.current = null;
    touchStartTime.current = null;
    hasSwiped.current = false;
  };

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  };
}
